import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface VideoPlayerProps {
  url?: string | null;
  storagePath?: string | null;
}

type Parsed =
  | { kind: "loom"; src: string }
  | { kind: "youtube"; src: string }
  | { kind: "vimeo"; src: string }
  | { kind: "file"; src: string }
  | { kind: "unknown"; src: string };

function stripQuery(u: string): string {
  const i = u.indexOf("?");
  return i === -1 ? u : u.slice(0, i);
}

export function parseVideoUrl(raw: string): Parsed {
  const url = raw.trim();
  if (!url) return { kind: "unknown", src: url };

  // Loom: loom.com/share/<uuid>
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) {
    return { kind: "loom", src: `https://www.loom.com/embed/${loomMatch[1]}` };
  }

  // YouTube: youtu.be/<id>
  const ytShort = url.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
  if (ytShort) {
    return { kind: "youtube", src: `https://www.youtube.com/embed/${ytShort[1]}` };
  }
  // YouTube: youtube.com/watch?v=<id>
  const ytLong = url.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (ytLong && /youtube\.com/.test(url)) {
    return { kind: "youtube", src: `https://www.youtube.com/embed/${ytLong[1]}` };
  }
  // YouTube: youtube.com/embed/<id> already
  const ytEmbed = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
  if (ytEmbed) {
    return { kind: "youtube", src: `https://www.youtube.com/embed/${ytEmbed[1]}` };
  }

  // Vimeo: vimeo.com/<id> (numeric)
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return { kind: "vimeo", src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }

  // Direct file
  const clean = stripQuery(url).toLowerCase();
  if (/\.(mp4|mov|webm|m4v)$/.test(clean)) {
    return { kind: "file", src: url };
  }

  return { kind: "unknown", src: url };
}

export function VideoPlayer({ url, storagePath }: VideoPlayerProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // url wins on display
  const effectiveUrl = url?.trim() || null;

  useEffect(() => {
    let cancelled = false;
    if (!effectiveUrl && storagePath) {
      setLoading(true);
      setError(null);
      supabase.storage
        .from("hq-videos")
        .createSignedUrl(storagePath, 60 * 60)
        .then(({ data, error }) => {
          if (cancelled) return;
          if (error || !data?.signedUrl) {
            setError(error?.message ?? "Could not load video.");
            setSignedUrl(null);
          } else {
            setSignedUrl(data.signedUrl);
          }
          setLoading(false);
        });
    } else {
      setSignedUrl(null);
      setError(null);
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [effectiveUrl, storagePath]);

  if (!effectiveUrl && !storagePath) return null;

  const wrapperClass =
    "relative w-full max-w-[1024px] mx-auto overflow-hidden rounded-xl ring-1 ring-line bg-ink/5";
  const aspectStyle = { paddingBottom: "56.25%" } as const; // 16:9
  const innerClass = "absolute inset-0 h-full w-full";

  if (effectiveUrl) {
    const parsed = parseVideoUrl(effectiveUrl);
    if (parsed.kind === "loom" || parsed.kind === "youtube" || parsed.kind === "vimeo") {
      return (
        <div className={wrapperClass}>
          <div style={aspectStyle} />
          <iframe
            src={parsed.src}
            title="Video"
            className={innerClass}
            frameBorder={0}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      );
    }
    if (parsed.kind === "file") {
      return (
        <div className={wrapperClass}>
          <div style={aspectStyle} />
          <video controls className={`${innerClass} bg-black`} src={parsed.src} />
        </div>
      );
    }
    // Unknown URL — try iframe fallback
    return (
      <div className={wrapperClass}>
        <div style={aspectStyle} />
        <iframe src={parsed.src} title="Video" className={innerClass} allowFullScreen />
      </div>
    );
  }

  // storage path path
  return (
    <div className={wrapperClass}>
      <div style={aspectStyle} />
      <div className={`${innerClass} flex items-center justify-center bg-ink/10 text-sm text-gray2`}>
        {loading && <span>Loading video…</span>}
        {error && <span className="text-bad">{error}</span>}
        {!loading && !error && signedUrl && (
          <video controls className="h-full w-full bg-black" src={signedUrl} />
        )}
      </div>
    </div>
  );
}

export default VideoPlayer;
